import { defineComponent as _defineComponent } from 'vue'
import useResponsiveness from '@/mixins/useResponsiveness';
import { ProfileStoreKey } from '@/models/Profile';
import useProfileState from '@/state/profile/profileState';
import { DELETED_USER_FULLNAME } from '@/utils/user';
import {
  computed, onBeforeUnmount, onMounted, watch, watchEffect
} from 'vue';
import ProfileFreelancer from '@/views/profile/components/profile-types/ProfileFreelancer.vue';
import ProfileGuest from '@/views/profile/components/profile-types/ProfileGuest.vue';
import ProfileCompany from '@/views/profile/components/profile-types/ProfileCompany.vue';
import { useRouter } from 'vue2-helpers/vue-router';
import BCXConfirm from '@/components/molecules/BCXConfirm.vue';
import useIsLeavingDirty from '@/utils/isLeavingDirty';
import useUser from '@/mixins/useUser';
import useI18n from '@/mixins/useI18n';
import usePageState from '@/state/pageState';
import useProfilePreview, { PreviewableSection } from '@/state/profile/profilePreview';
import useProfilePdfUploadPopup from '@/views/profile/composables/useProfilePdfUploadPopup';
import MarvinInfoBox from '@/views/recommendations/components/MarvinInfoBox.vue';
import BCXMarkdown from '@/components/molecules/BCXMarkdown.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'Profile',
  setup(__props) {


const { t } = useI18n();
const {
  init, user, profile, isLoading, editSections
} = useProfileState(ProfileStoreKey.PROFILE);
const router = useRouter();
const { isMobileWidth } = useResponsiveness();
const { pageNotFound } = usePageState();
init();

const {
  isCustomer, isGuest, isEmployee
} = useUser(user);

const showProfile = computed(() => !!user.value && !!profile.value);

const checkForDeletedUser = async () => {
  const ivUserLoaded = setInterval(() => {
    if (!isLoading.value) {
      clearInterval(ivUserLoaded);
      if ((user.value?.fullname ?? '').trim().toLowerCase() === DELETED_USER_FULLNAME) {
        router.push('/user-search');
      }
    }
  }, 250);
};

watch(() => user.value?.fullname, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    checkForDeletedUser();
  }
});

const {
  hasAnyUnresolved, discardAll, previewProfile, avatarPreview
} = useProfilePreview();
const {
  isLeavingDirty, isLeavingMessage, onConfirm, isDirty
} = useIsLeavingDirty();
const { open, consumeOpenOnProfileVisit } = useProfilePdfUploadPopup();
let openPopupTimeout: ReturnType<typeof setTimeout> | null = null;

// Two sources can make the page dirty: an open profile editor (excluding
// the always-open `basic` block), and AI preview slots awaiting Annehmen
// / Editieren / Löschen. The leave guard fires on either.
const hasEditorDirty = computed(() => editSections.value.length > 1
  || (editSections.value.length === 1 && !editSections.value.includes('basic')));

watchEffect(() => {
  isDirty.value = hasEditorDirty.value || hasAnyUnresolved.value;
});

// When the user confirms leaving with unresolved AI previews, wipe them
// so they don't reappear on the next visit (decision A: previews are
// client-side only). Swap the leave-confirm message to the AI-specific
// copy when previews are the active driver.
const leaveMessage = computed(() => (
  hasAnyUnresolved.value && !hasEditorDirty.value
    ? ''
    : isLeavingMessage.value
));

const previewSectionOrder: PreviewableSection[] = [
  'basic',
  'projects',
  'skills',
  'references',
  'educations',
  'certificates',
  'projectPreferences',
];

const unresolvedPreviewSections = computed<PreviewableSection[]>(() => previewSectionOrder.filter((section) => {
  if (section === 'basic') {
    return previewProfile.value.basic !== undefined || !!avatarPreview.value;
  }

  switch (section) {
    case 'projects': return previewProfile.value.experiences !== undefined;
    case 'skills': return previewProfile.value.skills !== undefined;
    case 'references': return previewProfile.value.references !== undefined;
    case 'educations': return previewProfile.value.educations !== undefined;
    case 'certificates': return previewProfile.value.certificates !== undefined;
    case 'projectPreferences': return previewProfile.value.projectPreferences !== undefined;
    default: return false;
  }
}));

const onLeaveConfirm = (confirmed: boolean) => {
  if (confirmed && hasAnyUnresolved.value) discardAll();
  onConfirm(confirmed);
};

onMounted(() => {
  checkForDeletedUser();
});

watch(showProfile, (visible) => {
  if (!visible) return;
  if (!consumeOpenOnProfileVisit()) return;

  if (openPopupTimeout) clearTimeout(openPopupTimeout);
  openPopupTimeout = setTimeout(() => {
    open();
    openPopupTimeout = null;
  }, 500);
}, { immediate: true });

// check if loading of profile fails:
watch(isLoading, (is) => {
  if (!is && !profile.value) {
    pageNotFound.value = true;
    router.push('/user-search');
  }
});

onBeforeUnmount(() => {
  if (openPopupTimeout) clearTimeout(openPopupTimeout);
});


return { __sfc: true,t, init, user, profile, isLoading, editSections, router, isMobileWidth, pageNotFound, isCustomer, isGuest, isEmployee, showProfile, checkForDeletedUser, hasAnyUnresolved, discardAll, previewProfile, avatarPreview, isLeavingDirty, isLeavingMessage, onConfirm, isDirty, open, consumeOpenOnProfileVisit, openPopupTimeout, hasEditorDirty, leaveMessage, previewSectionOrder, unresolvedPreviewSections, onLeaveConfirm, ProfileFreelancer, ProfileGuest, ProfileCompany, BCXConfirm, MarvinInfoBox, BCXMarkdown }
}

})